import {
  Alert,
  Box,
  Button,
  Card,
  Combobox,
  createListCollection,
  Heading,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Checkbox,
  CheckboxMultiSelect,
  type CheckboxMultiSelectOption,
  createTranslator,
  Field,
  type TranslateFn,
} from '@mangovoice/mango-ui'

import type { UserApi } from '../api/userApi'
import type {
  UserAssignedRole,
  UserCreate,
  UserDetail,
  UserRole,
  UserUpdate,
} from '../types/user.types'

// Stable empty-array default. Using an inline `= []` default creates a new
// reference every render, which re-triggers the edit-mode rehydration effect
// below (it depends on `assignedRoles`), causing an infinite render loop when
// the caller does not pass the prop.
const EMPTY_ASSIGNED_ROLES: UserAssignedRole[] = []

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserFormState {
  first_name: string
  last_name: string
  username: string
  email: string
  is_staff: boolean
  is_superuser: boolean
  role: string
  pbx: string
  pin: string
  sending_fax_email: string
  sending_fax_number: string
  fax_title: string
  pbx_user_groups: number[]
}

export interface PbxOption {
  id: number
  name: string
}

export interface UserGroupOption {
  id: number
  name: string
  pbx_id: number
}

export interface PhoneNumberOption {
  id: number
  name: string
  number: string
}

export interface UserFormProps {
  api: UserApi
  mode: 'create' | 'edit'
  user?: UserDetail | null
  roles: UserRole[]
  assignedRoles?: UserAssignedRole[]
  pbxOptions?: PbxOption[]
  userGroupOptions?: UserGroupOption[]
  /**
   * Phone numbers on the currently-selected PBX, used to populate the
   * "Sending Fax Number" autocomplete. The parent page is responsible for
   * fetching these in response to `onPbxChange` callbacks.
   */
  phoneNumberOptions?: PhoneNumberOption[]
  isPhoneNumbersLoading?: boolean
  /** Fires whenever the selected PBX changes so the parent can refetch phone numbers. */
  onPbxChange?: (pbxId: number | null) => void
  defaultPbxId?: number
  isSubmitting: boolean
  onSubmit: (data: UserCreate | UserUpdate) => void
  onSubmitAndCreateAnother?: (data: UserCreate, onSuccess: () => void) => void
  onCancel: () => void
  isStaff: boolean
  isSuperuser: boolean
  /**
   * Whether the current user can assign a role to the target user. When true,
   * the role dropdown is visible and `role` is included in the payload.
   * Defaults to `isStaff` for backward compatibility; consumers should pass
   * `true` for non-staff administrators who still need to set roles.
   */
  canAssignRole?: boolean
  /**
   * When provided, the PBX combobox becomes a controlled, server-side search.
   * The consumer is responsible for fetching matching PBXes and passing them
   * via `pbxOptions`; the form will skip its internal client-side filtering.
   */
  onPbxSearchTermChange?: (term: string) => void
  /** Loading state for the controlled PBX search (only relevant when `onPbxSearchTermChange` is set). */
  isPbxSearchLoading?: boolean
  /**
   * Optional translation function (e.g. `react-i18next`'s `t`). When
   * omitted, every string falls back to its English `defaultValue`. Keys
   * all live under `users.form.*` so consuming apps can translate in a
   * single namespace.
   */
  t?: TranslateFn
  /**
   * Server-side validation errors keyed by field name (e.g. from a 400 response).
   * Non-field errors should be keyed as `_nonField`.
   * Use `extractFieldErrors` from the hooks module to produce this map.
   */
  serverErrors?: Record<string, string>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultState(): UserFormState {
  return {
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    is_staff: false,
    is_superuser: false,
    role: '',
    pbx: '',
    pin: '',
    sending_fax_email: '',
    sending_fax_number: '',
    fax_title: '',
    pbx_user_groups: [],
  }
}

const LEGACY_ROLE_ORDER = [
  'Super Administrator',
  'Senior Administrator',
  'Administrator',
  'Portal Unrestricted',
  'Portal Restricted',
  'Portal Limited',
] as const

type LegacyRoleLabel = (typeof LEGACY_ROLE_ORDER)[number]

const LEGACY_ROLE_ALIASES: Record<string, LegacyRoleLabel> = {
  'internal administrator': 'Senior Administrator',
}

function normalizeRoleValue(value?: string | null): string {
  return value?.trim().toLowerCase() ?? ''
}

function getRoleSourceLabels(role: UserRole): string[] {
  return [role.resource_type, role.type, role.name].filter(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  )
}

function getPrimaryRoleSourceLabel(role: UserRole): string {
  const labels = getRoleSourceLabels(role)
  return labels[0] ?? ''
}

function getRoleId(role: UserRole): number | null {
  return typeof role.id === 'number' && Number.isFinite(role.id)
    ? role.id
    : null
}

function toLegacyRoleLabelFromSources(
  sourceValues: (string | null | undefined)[],
): LegacyRoleLabel | null {
  const normalizedValues = sourceValues
    .map((value) => normalizeRoleValue(value))
    .filter(Boolean)

  if (normalizedValues.length === 0) return null

  for (const normalizedValue of normalizedValues) {
    if (LEGACY_ROLE_ALIASES[normalizedValue]) {
      return LEGACY_ROLE_ALIASES[normalizedValue]
    }

    const exactLegacyLabel = LEGACY_ROLE_ORDER.find(
      (role) => normalizeRoleValue(role) === normalizedValue,
    )
    if (exactLegacyLabel) {
      return exactLegacyLabel
    }
  }

  if (normalizedValues.some((value) => value.includes('portal unrestricted'))) {
    return 'Portal Unrestricted'
  }

  if (normalizedValues.some((value) => value.includes('portal restricted'))) {
    return 'Portal Restricted'
  }

  if (normalizedValues.some((value) => value.includes('portal limited'))) {
    return 'Portal Limited'
  }

  if (
    normalizedValues.some(
      (value) =>
        value === 'super_admin' ||
        value === 'super_user' ||
        value.includes('super administrator'),
    )
  ) {
    return 'Super Administrator'
  }

  if (
    normalizedValues.some(
      (value) =>
        value === 'mango' ||
        value.includes('internal administrator') ||
        value.includes('senior administrator'),
    )
  ) {
    return 'Senior Administrator'
  }

  if (
    normalizedValues.some(
      (value) =>
        value === 'admin' ||
        value === 'administrator' ||
        value.includes('administrator'),
    )
  ) {
    return 'Administrator'
  }

  return null
}

function toLegacyRoleLabel(value?: string | null): LegacyRoleLabel | null {
  return toLegacyRoleLabelFromSources([value])
}

function getCanonicalAssignedRoleLabel(
  assignedRoles: UserAssignedRole[],
): LegacyRoleLabel | null {
  for (const assignedRole of assignedRoles) {
    const canonicalRole = toLegacyRoleLabelFromSources([
      assignedRole.type,
      assignedRole.name,
    ])
    if (canonicalRole) return canonicalRole
  }

  return null
}

function userToFormState(
  user: UserDetail,
  roles: UserRole[],
  assignedRoles: UserAssignedRole[],
): UserFormState {
  const userRoleCanonical =
    getCanonicalAssignedRoleLabel(assignedRoles) ?? toLegacyRoleLabel(user.role)
  const userRoleRaw = normalizeRoleValue(user.role)

  const matchedRole = roles.find((role) => {
    const roleSources = getRoleSourceLabels(role)
    const roleCanonical = toLegacyRoleLabelFromSources(roleSources)
    if (userRoleCanonical && roleCanonical) {
      return roleCanonical === userRoleCanonical
    }

    return roleSources.some(
      (sourceLabel) => normalizeRoleValue(sourceLabel) === userRoleRaw,
    )
  })

  return {
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    email: user.email || '',
    is_staff: user.is_staff,
    is_superuser: user.is_superuser,
    role: matchedRole ? matchedRole.id.toString() : '',
    pbx: '',
    pin: '',
    sending_fax_email: '',
    sending_fax_number: '',
    fax_title: '',
    pbx_user_groups: [],
  }
}

const fieldLabelStyles = {
  fontSize: 'sm',
  fontWeight: 'semibold',
  color: 'fg.default',
}

const helperTextStyles = {
  fontSize: 'xs',
  color: 'fg.muted',
  lineHeight: 'shorter',
}

const inputStyles = {
  h: '36px',
  px: 3,
  bg: 'bg.default',
  borderColor: 'border.default',
  borderRadius: 'md',
  _placeholder: { color: 'fg.subtle' },
  _hover: { borderColor: 'border.emphasized' },
  _focusVisible: {
    borderColor: 'brand.solid',
    boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
    outline: 'none',
  },
}

// ─── Component ────────────────────────────────────────────────────────────────

export const UserForm: React.FC<UserFormProps> = ({
  mode,
  user,
  roles,
  assignedRoles = EMPTY_ASSIGNED_ROLES,
  pbxOptions = [],
  userGroupOptions = [],
  phoneNumberOptions = [],
  isPhoneNumbersLoading = false,
  onPbxChange,
  defaultPbxId,
  isSubmitting,
  onSubmit,
  onSubmitAndCreateAnother,
  onCancel,
  isStaff,
  isSuperuser,
  canAssignRole: canAssignRoleProp,
  onPbxSearchTermChange,
  isPbxSearchLoading = false,
  t,
  serverErrors,
}) => {
  const tr = useMemo(() => createTranslator(t), [t])
  const canAssignRole = canAssignRoleProp ?? isStaff
  const isPbxSearchControlled = typeof onPbxSearchTermChange === 'function'
  const showPbxCombobox = defaultPbxId == null
  const isEdit = mode === 'edit'
  // Legacy users created before email was required may have no email on
  // file. Mirror the admin2 rule: email is editable until it is set, then
  // becomes immutable. (The API enforces the same contract.)
  const canEditEmail = !isEdit || !user?.email
  const firstNameRef = useRef<HTMLInputElement>(null)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const [form, setForm] = useState<UserFormState>(() => {
    if (isEdit && user) return userToFormState(user, roles, assignedRoles)
    const state = getDefaultState()
    if (defaultPbxId) state.pbx = defaultPbxId.toString()
    return state
  })
  const [pbxInputValue, setPbxInputValue] = useState('')
  const [pbxSearchTerm, setPbxSearchTerm] = useState('')
  const [isPrefillLabelInitialized, setIsPrefillLabelInitialized] =
    useState(false)
  const [isDefaultRoleInitialized, setIsDefaultRoleInitialized] =
    useState(false)

  useEffect(() => {
    if (isEdit && user) {
      setForm(userToFormState(user, roles, assignedRoles))
    }
  }, [assignedRoles, isEdit, user, roles])

  useEffect(() => {
    if (isEdit || !showPbxCombobox || isPrefillLabelInitialized || !form.pbx)
      return

    const selectedPbxOption = pbxOptions.find(
      (option) => option.id.toString() === form.pbx,
    )
    if (!selectedPbxOption) return

    setPbxInputValue(selectedPbxOption.name)
    setIsPrefillLabelInitialized(true)
  }, [form.pbx, isEdit, isPrefillLabelInitialized, pbxOptions, showPbxCombobox])

  const updateField = useCallback(
    <K extends keyof UserFormState>(field: K, value: UserFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const selectedPbxId = form.pbx ? parseInt(form.pbx, 10) : null
  const filteredGroups = selectedPbxId
    ? userGroupOptions.filter((g) => g.pbx_id === selectedPbxId)
    : []

  const userGroupCheckboxOptions = useMemo<CheckboxMultiSelectOption[]>(
    () => filteredGroups.map((group) => ({ id: group.id, label: group.name })),
    [filteredGroups],
  )

  useEffect(() => {
    if (isEdit) return
    onPbxChange?.(selectedPbxId)
  }, [isEdit, onPbxChange, selectedPbxId])

  const [faxNumberSearchTerm, setFaxNumberSearchTerm] = useState('')
  const normalizedFaxNumberSearchTerm = React.useMemo(
    () => faxNumberSearchTerm.trim().toLowerCase(),
    [faxNumberSearchTerm],
  )

  const filteredFaxNumberOptions = React.useMemo(() => {
    if (!normalizedFaxNumberSearchTerm) return phoneNumberOptions
    return phoneNumberOptions.filter((option) => {
      const name = option.name.toLowerCase()
      const number = option.number.toLowerCase()
      return (
        name.includes(normalizedFaxNumberSearchTerm) ||
        number.includes(normalizedFaxNumberSearchTerm)
      )
    })
  }, [normalizedFaxNumberSearchTerm, phoneNumberOptions])

  const faxNumberCollection = React.useMemo(
    () =>
      createListCollection<PhoneNumberOption>({
        items: filteredFaxNumberOptions,
        itemToValue: (item) => item.number,
        itemToString: (item) =>
          item.name ? `${item.name} (${item.number})` : item.number,
      }),
    [filteredFaxNumberOptions],
  )

  const faxNumberInputValue = React.useMemo(() => {
    if (!form.sending_fax_number) return ''
    const match = phoneNumberOptions.find(
      (option) => option.number === form.sending_fax_number,
    )
    if (match)
      return match.name ? `${match.name} (${match.number})` : match.number
    return form.sending_fax_number
  }, [form.sending_fax_number, phoneNumberOptions])

  const normalizedPbxSearchTerm = pbxSearchTerm.trim().toLowerCase()

  const filteredPbxOptions = React.useMemo(() => {
    // When the consumer drives the search, `pbxOptions` is already the
    // server-filtered set — don't filter again on the client.
    if (isPbxSearchControlled) return pbxOptions
    if (!normalizedPbxSearchTerm) return pbxOptions

    return pbxOptions.filter((option) =>
      option.name.toLowerCase().includes(normalizedPbxSearchTerm),
    )
  }, [pbxOptions, normalizedPbxSearchTerm, isPbxSearchControlled])

  const visiblePbxOptions = React.useMemo(() => {
    if (!form.pbx) return filteredPbxOptions

    const selectedPbxOption = pbxOptions.find(
      (option) => option.id.toString() === form.pbx,
    )
    if (!selectedPbxOption) return filteredPbxOptions

    const alreadyIncluded = filteredPbxOptions.some(
      (option) => option.id === selectedPbxOption.id,
    )
    if (alreadyIncluded) return filteredPbxOptions

    return [selectedPbxOption, ...filteredPbxOptions]
  }, [filteredPbxOptions, form.pbx, pbxOptions])

  const pbxCollection = React.useMemo(
    () =>
      createListCollection<PbxOption>({
        items: visiblePbxOptions,
        itemToValue: (item) => item.id.toString(),
        itemToString: (item) => item.name,
      }),
    [visiblePbxOptions],
  )

  const legacyRoleOptions = React.useMemo(() => {
    const byCanonicalLabel = new Map<
      LegacyRoleLabel,
      { id: number; sourceLabel: string }
    >()

    for (const role of roles) {
      const roleId = getRoleId(role)
      if (roleId == null) continue

      const sourceLabels = getRoleSourceLabels(role)
      const sourceLabel = getPrimaryRoleSourceLabel(role)
      const canonicalLabel = toLegacyRoleLabelFromSources(sourceLabels)
      if (!canonicalLabel) continue

      const existing = byCanonicalLabel.get(canonicalLabel)
      if (!existing) {
        byCanonicalLabel.set(canonicalLabel, { id: roleId, sourceLabel })
        continue
      }

      const existingIsExactMatch =
        normalizeRoleValue(existing.sourceLabel) ===
        normalizeRoleValue(canonicalLabel)
      const candidateIsExactMatch = sourceLabels.some(
        (label) =>
          normalizeRoleValue(label) === normalizeRoleValue(canonicalLabel),
      )

      if (!existingIsExactMatch && candidateIsExactMatch) {
        byCanonicalLabel.set(canonicalLabel, { id: roleId, sourceLabel })
      }
    }

    return LEGACY_ROLE_ORDER.map((canonicalLabel) => {
      const role = byCanonicalLabel.get(canonicalLabel)
      if (!role) return null

      return {
        id: role.id,
        label: canonicalLabel,
      }
    }).filter(
      (
        role,
      ): role is {
        id: number
        label: LegacyRoleLabel
      } => role !== null,
    )
  }, [roles])

  useEffect(() => {
    if (isEdit || !canAssignRole || isDefaultRoleInitialized) return

    if (form.role) {
      setIsDefaultRoleInitialized(true)
      return
    }

    const defaultRoleOption = legacyRoleOptions.find(
      (role) => role.label === 'Portal Limited',
    )
    if (!defaultRoleOption) return

    updateField('role', defaultRoleOption.id.toString())
    setIsDefaultRoleInitialized(true)
  }, [
    form.role,
    isDefaultRoleInitialized,
    isEdit,
    canAssignRole,
    legacyRoleOptions,
    updateField,
  ])

  const handleGroupToggle = useCallback((groupId: number) => {
    setForm((prev) => {
      const next = prev.pbx_user_groups.includes(groupId)
        ? prev.pbx_user_groups.filter((id) => id !== groupId)
        : [...prev.pbx_user_groups, groupId]
      return { ...prev, pbx_user_groups: next }
    })
  }, [])

  const partialReset = useCallback(() => {
    setForm((prev) => ({
      ...getDefaultState(),
      first_name: prev.first_name,
      last_name: prev.last_name,
      username: prev.username,
      is_staff: prev.is_staff,
      is_superuser: prev.is_superuser,
      role: prev.role,
      pbx: prev.pbx,
      pbx_user_groups: prev.pbx_user_groups,
    }))
    setPbxSearchTerm('')
    requestAnimationFrame(() => firstNameRef.current?.focus())
  }, [])

  const buildCreatePayload = useCallback((): UserCreate => {
    const normalizedEmail = form.email.trim()
    const data: UserCreate = {
      first_name: form.first_name,
      last_name: form.last_name,
      username: form.username,
      pbx: parseInt(form.pbx, 10),
    }
    if (normalizedEmail) data.email = normalizedEmail
    if (isStaff) {
      data.is_staff = form.is_staff
    }
    if (canAssignRole) {
      data.role = form.role ? parseInt(form.role, 10) : null
    }
    if (isSuperuser) {
      data.is_superuser = form.is_superuser
    }
    if (form.pin) data.pin = parseInt(form.pin, 10)
    if (form.sending_fax_email) data.sending_fax_email = form.sending_fax_email
    if (form.sending_fax_number)
      data.sending_fax_number = form.sending_fax_number
    if (form.fax_title) data.fax_title = form.fax_title
    if (form.pbx_user_groups.length > 0)
      data.pbx_user_groups = form.pbx_user_groups
    return data
  }, [form, canAssignRole, isStaff, isSuperuser])

  const handleSubmit = useCallback(() => {
    if (isEdit) {
      const data: UserUpdate = {
        first_name: form.first_name,
        last_name: form.last_name,
      }
      // Only legacy users with no email on file can backfill one; the API
      // ignores `email` otherwise. Keep the payload tight to avoid noise.
      if (canEditEmail) {
        const trimmedEmail = form.email.trim()
        if (trimmedEmail) data.email = trimmedEmail
      }
      if (isStaff) {
        data.is_staff = form.is_staff
      }
      if (canAssignRole) {
        data.role = form.role ? parseInt(form.role, 10) : null
      }
      if (isSuperuser) {
        data.is_superuser = form.is_superuser
      }
      onSubmit(data)
    } else {
      onSubmit(buildCreatePayload())
    }
  }, [
    buildCreatePayload,
    canAssignRole,
    canEditEmail,
    form,
    isEdit,
    isStaff,
    isSuperuser,
    onSubmit,
  ])

  const roleOptionsAvailable = legacyRoleOptions.length > 0

  const canSubmit =
    form.first_name.trim().length > 0 &&
    form.last_name.trim().length > 0 &&
    (isEdit || (form.username.trim().length >= 4 && form.pbx)) &&
    (!canAssignRole || roleOptionsAvailable) &&
    !isSubmitting

  const showErrors = hasAttemptedSubmit || !!serverErrors
  const isFirstNameInvalid = form.first_name.trim().length === 0
  const isLastNameInvalid = form.last_name.trim().length === 0
  const isUsernameInvalid = !isEdit && form.username.trim().length < 4
  const isPbxInvalid = !isEdit && showPbxCombobox && !form.pbx

  const handleCreateAnother = useCallback(() => {
    setHasAttemptedSubmit(true)
    if (!canSubmit) return
    onSubmitAndCreateAnother?.(buildCreatePayload(), partialReset)
  }, [buildCreatePayload, canSubmit, onSubmitAndCreateAnother, partialReset])

  const onFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setHasAttemptedSubmit(true)
      if (!canSubmit) return
      handleSubmit()
    },
    [canSubmit, handleSubmit],
  )

  return (
    <form onSubmit={onFormSubmit}>
      <Card.Root>
        <Card.Header px={5} py={4}>
          <Heading as="h1" fontSize="lg" fontWeight="semibold">
            {isEdit
              ? tr('users.form.heading.editUser', 'Edit User')
              : tr('users.form.heading.newUser', 'New User')}
          </Heading>
        </Card.Header>

        <Card.Body px={5} py={4}>
          <VStack align="stretch" gap={4}>
            {(() => {
              const nonFieldMsg =
                serverErrors?._nonField || serverErrors?.is_staff
              if (!nonFieldMsg) return null
              // Don't show the generic envelope message when field-specific
              // errors are already displayed inline — it adds no useful info.
              const hasFieldErrors =
                serverErrors &&
                Object.keys(serverErrors).some((k) => k !== '_nonField')
              if (hasFieldErrors) return null
              return (
                <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Description>{nonFieldMsg}</Alert.Description>
                </Alert.Root>
              )
            })()}
            {/* Name fields */}
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={3}
            >
              <Field.Root
                required
                gap={1}
                invalid={
                  (showErrors && isFirstNameInvalid) ||
                  !!serverErrors?.first_name
                }
              >
                <Field.Label css={fieldLabelStyles}>
                  {tr('users.form.field.firstName', 'First Name')}
                </Field.Label>
                <Input
                  ref={firstNameRef}
                  value={form.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  maxLength={50}
                  {...inputStyles}
                />
                {showErrors && isFirstNameInvalid && (
                  <Field.ErrorText>
                    {tr(
                      'users.form.error.firstNameRequired',
                      'First name is required.',
                    )}
                  </Field.ErrorText>
                )}
                {serverErrors?.first_name && (
                  <Field.ErrorText>{serverErrors.first_name}</Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root
                required
                gap={1}
                invalid={
                  (showErrors && isLastNameInvalid) || !!serverErrors?.last_name
                }
              >
                <Field.Label css={fieldLabelStyles}>
                  {tr('users.form.field.lastName', 'Last Name')}
                </Field.Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  maxLength={50}
                  {...inputStyles}
                />
                {showErrors && isLastNameInvalid && (
                  <Field.ErrorText>
                    {tr(
                      'users.form.error.lastNameRequired',
                      'Last name is required.',
                    )}
                  </Field.ErrorText>
                )}
                {serverErrors?.last_name && (
                  <Field.ErrorText>{serverErrors.last_name}</Field.ErrorText>
                )}
              </Field.Root>
            </Box>

            {/* Username & Email */}
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={3}
            >
              <Field.Root
                required={!isEdit}
                gap={1}
                invalid={
                  (showErrors && isUsernameInvalid) || !!serverErrors?.username
                }
              >
                <Field.Label css={fieldLabelStyles}>
                  {tr('users.form.field.username', 'Username')}
                </Field.Label>
                <Input
                  value={form.username}
                  onChange={(e) => updateField('username', e.target.value)}
                  disabled={isEdit}
                  {...inputStyles}
                />
                {!isEdit && (
                  <Field.HelperText css={helperTextStyles}>
                    {tr(
                      'users.form.field.usernameHelper',
                      'Required. 4 - 150 characters. Letters, numbers, periods, or dashes only. Note: Username cannot be changed after creation.',
                    )}
                  </Field.HelperText>
                )}
                {showErrors && isUsernameInvalid && (
                  <Field.ErrorText>
                    {tr(
                      'users.form.error.usernameMinLength',
                      'Username must be at least 4 characters.',
                    )}
                  </Field.ErrorText>
                )}
                {serverErrors?.username && (
                  <Field.ErrorText>{serverErrors.username}</Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root gap={1} invalid={!!serverErrors?.email}>
                <Field.Label css={fieldLabelStyles}>
                  {tr('users.form.field.email', 'Email')}
                </Field.Label>
                <Input
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  type="email"
                  disabled={!canEditEmail}
                  {...inputStyles}
                />
                {canEditEmail && (
                  <Field.HelperText css={helperTextStyles}>
                    {tr(
                      'users.form.field.emailHelper',
                      'Optional. Email cannot be changed once set.',
                    )}
                  </Field.HelperText>
                )}
                {serverErrors?.email && (
                  <Field.ErrorText>{serverErrors.email}</Field.ErrorText>
                )}
              </Field.Root>
            </Box>

            {/* Staff flags (Mango staff only) */}
            {isStaff && (
              <VStack align="stretch" gap={3}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Checkbox
                    checked={form.is_staff}
                    onCheckedChange={(details) =>
                      updateField('is_staff', details.checked === true)
                    }
                    display="flex"
                    alignItems="center"
                    gap={0}
                    pt={0.5}
                  >
                    <Text as="span" fontSize="sm" fontWeight="semibold">
                      {tr('users.form.field.isStaff', 'Is Mango Staff?')}
                    </Text>
                  </Checkbox>
                </Box>
                <Text css={helperTextStyles} pl={6}>
                  {tr(
                    'users.form.field.staffHelperIntro',
                    'This designation grants full access to the system with a few exceptions:',
                  )}
                  <br />-{' '}
                  {tr(
                    'users.form.field.staffHelperBullet1',
                    'The user cannot assign the designation of Mango Superuser to users.',
                  )}
                  <br />-{' '}
                  {tr(
                    'users.form.field.staffHelperBullet2',
                    'The user cannot modify Mango Superusers.',
                  )}
                  <br />-{' '}
                  {tr(
                    'users.form.field.staffHelperBullet3',
                    'The user cannot modify servers, providers, or device manufacturers.',
                  )}
                </Text>
                {isSuperuser && (
                  <Checkbox
                    checked={form.is_superuser}
                    onCheckedChange={(details) =>
                      updateField('is_superuser', details.checked === true)
                    }
                    display="flex"
                    alignItems="center"
                    gap={0}
                  >
                    <Text as="span" fontSize="sm" fontWeight="semibold">
                      {tr('users.form.field.isSuperuser', 'Mango Superuser')}
                    </Text>
                  </Checkbox>
                )}
              </VStack>
            )}

            {/* Role selection */}
            {canAssignRole && (
              <Field.Root gap={1} invalid={!!serverErrors?.role}>
                <Field.Label css={fieldLabelStyles}>
                  {tr('users.form.field.role', 'Role')}
                </Field.Label>
                <NativeSelect.Root
                  size="md"
                  disabled={legacyRoleOptions.length === 0}
                >
                  <NativeSelect.Field
                    value={form.role}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      updateField('role', e.target.value)
                    }
                    {...inputStyles}
                  >
                    <option value="">
                      {legacyRoleOptions.length === 0
                        ? tr(
                            'users.form.field.roleUnavailable',
                            'Role options unavailable',
                          )
                        : tr('users.form.field.rolePlaceholder', '------')}
                    </option>
                    {legacyRoleOptions.map((role) => (
                      <option key={role.id} value={role.id.toString()}>
                        {role.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                {legacyRoleOptions.length === 0 && (
                  <Field.HelperText color="fg.error" fontSize="xs">
                    {tr(
                      'users.form.field.roleUnavailableHelper',
                      'Role options are unavailable.',
                    )}
                  </Field.HelperText>
                )}
                <Field.HelperText css={helperTextStyles}>
                  {tr(
                    'users.form.field.roleWarning',
                    'WARNING: This role applies to all PBXes the user belongs to. Carefully consider the implications before making changes.',
                  )}
                </Field.HelperText>
                {serverErrors?.role && (
                  <Field.ErrorText>{serverErrors.role}</Field.ErrorText>
                )}
              </Field.Root>
            )}

            {/* PBX-specific fields (create mode only) */}
            {!isEdit && (
              <>
                {showPbxCombobox && (
                  <Heading as="h2" fontSize="md" fontWeight="semibold">
                    {tr('users.form.section.pbxAssignment', 'PBX Assignment')}
                  </Heading>
                )}
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: '1fr',
                    md: showPbxCombobox ? '1fr 1fr' : '1fr',
                  }}
                  gap={3}
                >
                  {showPbxCombobox && (
                    <Field.Root
                      required
                      gap={1}
                      invalid={
                        (showErrors && isPbxInvalid) || !!serverErrors?.pbx
                      }
                    >
                      <Field.Label css={fieldLabelStyles}>
                        {tr('users.form.field.pbx', 'PBX')}
                      </Field.Label>
                      <Combobox.Root
                        collection={pbxCollection}
                        value={form.pbx ? [form.pbx] : []}
                        inputValue={pbxInputValue}
                        onValueChange={(details) => {
                          const nextPbxValue = details.value[0] ?? ''
                          updateField('pbx', nextPbxValue)
                          updateField('pbx_user_groups', [])
                          updateField('sending_fax_number', '')
                          setFaxNumberSearchTerm('')
                          setPbxSearchTerm('')
                          if (isPbxSearchControlled) {
                            onPbxSearchTermChange?.('')
                          }
                          if (!nextPbxValue) {
                            setPbxInputValue('')
                            return
                          }

                          const selectedPbxOption = pbxOptions.find(
                            (option) => option.id.toString() === nextPbxValue,
                          )
                          setPbxInputValue(selectedPbxOption?.name ?? '')
                        }}
                        onInputValueChange={(details) => {
                          setPbxInputValue(details.inputValue)
                          setPbxSearchTerm(details.inputValue)
                          if (isPbxSearchControlled) {
                            onPbxSearchTermChange?.(details.inputValue)
                          }
                        }}
                        inputBehavior="autohighlight"
                        openOnClick
                        width="100%"
                        size="md"
                      >
                        <Combobox.Control>
                          <Combobox.Input
                            placeholder={tr(
                              'users.form.field.pbxSearchPlaceholder',
                              'Search PBX by name...',
                            )}
                            {...inputStyles}
                            aria-label={tr(
                              'users.form.field.pbxSearchAriaLabel',
                              'Search PBX options by name',
                            )}
                          />
                          <Combobox.IndicatorGroup>
                            <Combobox.Trigger>
                              <ChevronsUpDown />
                            </Combobox.Trigger>
                          </Combobox.IndicatorGroup>
                        </Combobox.Control>
                        <Combobox.Positioner>
                          <Combobox.Content maxH="280px" overflowY="auto">
                            {isPbxSearchLoading ? (
                              <Combobox.Empty>
                                {tr(
                                  'users.form.combobox.searching',
                                  'Searching...',
                                )}
                              </Combobox.Empty>
                            ) : isPbxSearchControlled &&
                              !pbxSearchTerm.trim() ? (
                              <Combobox.Empty>
                                {tr(
                                  'users.form.combobox.typeToSearchPbx',
                                  'Type to search PBX...',
                                )}
                              </Combobox.Empty>
                            ) : visiblePbxOptions.length === 0 ? (
                              <Combobox.Empty>
                                {tr(
                                  'users.form.combobox.noPbxMatches',
                                  'No PBX matches that search.',
                                )}
                              </Combobox.Empty>
                            ) : (
                              visiblePbxOptions.map((option) => (
                                <Combobox.Item key={option.id} item={option}>
                                  <Combobox.ItemText>
                                    {option.name}
                                  </Combobox.ItemText>
                                  <Combobox.ItemIndicator>
                                    <Check />
                                  </Combobox.ItemIndicator>
                                </Combobox.Item>
                              ))
                            )}
                          </Combobox.Content>
                        </Combobox.Positioner>
                      </Combobox.Root>
                      {showErrors && isPbxInvalid && (
                        <Field.ErrorText>
                          {tr(
                            'users.form.error.pbxRequired',
                            'A PBX must be selected.',
                          )}
                        </Field.ErrorText>
                      )}
                      {serverErrors?.pbx && (
                        <Field.ErrorText>{serverErrors.pbx}</Field.ErrorText>
                      )}
                    </Field.Root>
                  )}
                  <Field.Root gap={1} invalid={!!serverErrors?.pin}>
                    <Field.Label css={fieldLabelStyles}>
                      {tr('users.form.field.pin', 'PIN')}
                    </Field.Label>
                    <Input
                      value={form.pin}
                      onChange={(e) => updateField('pin', e.target.value)}
                      placeholder={tr(
                        'users.form.field.pinPlaceholder',
                        '4-digit PIN (1000-9999)',
                      )}
                      type="number"
                      {...inputStyles}
                    />
                    {serverErrors?.pin && (
                      <Field.ErrorText>{serverErrors.pin}</Field.ErrorText>
                    )}
                  </Field.Root>
                </Box>

                <Box
                  display="grid"
                  gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                  gap={3}
                >
                  <Field.Root gap={1}>
                    <Field.Label css={fieldLabelStyles}>
                      {tr('users.form.field.faxTitle', 'Fax Title')}
                    </Field.Label>
                    <Input
                      value={form.fax_title}
                      onChange={(e) => updateField('fax_title', e.target.value)}
                      placeholder={tr(
                        'users.form.field.faxTitlePlaceholder',
                        'Fax title',
                      )}
                      {...inputStyles}
                    />
                  </Field.Root>
                  <Field.Root
                    gap={1}
                    invalid={!!serverErrors?.sending_fax_email}
                  >
                    <Field.Label css={fieldLabelStyles}>
                      {tr(
                        'users.form.field.sendingFaxEmail',
                        'Sending Fax Email',
                      )}
                    </Field.Label>
                    <Input
                      value={form.sending_fax_email}
                      onChange={(e) =>
                        updateField('sending_fax_email', e.target.value)
                      }
                      placeholder={tr(
                        'users.form.field.sendingFaxEmailPlaceholder',
                        'Fax email address',
                      )}
                      {...inputStyles}
                    />
                    {serverErrors?.sending_fax_email && (
                      <Field.ErrorText>
                        {serverErrors.sending_fax_email}
                      </Field.ErrorText>
                    )}
                  </Field.Root>
                </Box>

                <Field.Root gap={1}>
                  <Field.Label css={fieldLabelStyles}>
                    {tr(
                      'users.form.field.sendingFaxNumber',
                      'Sending Fax Number',
                    )}
                  </Field.Label>
                  <Combobox.Root
                    collection={faxNumberCollection}
                    value={
                      form.sending_fax_number ? [form.sending_fax_number] : []
                    }
                    inputValue={faxNumberInputValue}
                    onValueChange={(details) => {
                      updateField('sending_fax_number', details.value[0] ?? '')
                      setFaxNumberSearchTerm('')
                    }}
                    onInputValueChange={(details) => {
                      setFaxNumberSearchTerm(details.inputValue)
                    }}
                    inputBehavior="autohighlight"
                    openOnClick
                    width="100%"
                    size="md"
                    disabled={!selectedPbxId}
                  >
                    <Combobox.Control>
                      <Combobox.Input
                        placeholder={
                          selectedPbxId
                            ? tr(
                                'users.form.field.searchFaxNumbers',
                                'Search fax numbers...',
                              )
                            : tr(
                                'users.form.field.selectPbxFirst',
                                'Select a PBX first',
                              )
                        }
                        {...inputStyles}
                        aria-label={tr(
                          'users.form.field.searchFaxNumbersAriaLabel',
                          'Search sending fax number options',
                        )}
                      />
                      <Combobox.IndicatorGroup>
                        <Combobox.Trigger>
                          <ChevronsUpDown />
                        </Combobox.Trigger>
                      </Combobox.IndicatorGroup>
                    </Combobox.Control>
                    <Combobox.Positioner>
                      <Combobox.Content maxH="280px" overflowY="auto">
                        <Combobox.Empty>
                          {isPhoneNumbersLoading
                            ? tr(
                                'users.form.combobox.loadingFaxNumbers',
                                'Loading fax numbers...',
                              )
                            : tr(
                                'users.form.combobox.noFaxNumbers',
                                'No fax numbers found for this PBX.',
                              )}
                        </Combobox.Empty>
                        {filteredFaxNumberOptions.map((option) => (
                          <Combobox.Item key={option.id} item={option}>
                            <Combobox.ItemText>
                              {option.name
                                ? `${option.name} (${option.number})`
                                : option.number}
                            </Combobox.ItemText>
                            <Combobox.ItemIndicator>
                              <Check />
                            </Combobox.ItemIndicator>
                          </Combobox.Item>
                        ))}
                      </Combobox.Content>
                    </Combobox.Positioner>
                  </Combobox.Root>
                </Field.Root>

                {/* User Groups.
                    NOTE: Intentionally NOT wrapped in Field.Root — see
                    CheckboxMultiSelect for the reusable picker; the heading
                    is a plain Text so every nested checkbox keeps its own
                    id (see EditPhonePermissionsDialog for the same
                    pattern). */}
                {selectedPbxId && filteredGroups.length > 0 && (
                  <Box>
                    <Text css={fieldLabelStyles} mb={1}>
                      {tr('users.form.field.userGroups', 'User Groups')}
                    </Text>
                    <CheckboxMultiSelect
                      options={userGroupCheckboxOptions}
                      selectedIds={form.pbx_user_groups}
                      onToggle={(id) => handleGroupToggle(id as number)}
                      placeholder={tr(
                        'users.form.userGroups.placeholder',
                        'Select user groups...',
                      )}
                      searchPlaceholder={tr(
                        'users.form.userGroups.searchPlaceholder',
                        'Filter user groups...',
                      )}
                      emptyLabel={tr(
                        'users.form.userGroups.emptyLabel',
                        'No user groups available for this PBX.',
                      )}
                      noResultsLabel={tr(
                        'users.form.userGroups.noResultsLabel',
                        'No user groups match your filter.',
                      )}
                      renderSummary={(count) =>
                        tr(
                          'users.form.userGroups.summary',
                          count === 1
                            ? `${count} group selected`
                            : `${count} groups selected`,
                          { count },
                        )
                      }
                    />
                  </Box>
                )}
              </>
            )}
          </VStack>
        </Card.Body>

        <Box px={5} py={4} borderTopWidth={1} borderColor="border.default">
          {hasAttemptedSubmit && !canSubmit && (
            <Text fontSize="xs" color="fg.muted" mb={3}>
              {tr(
                'users.form.error.fillRequired',
                'Please fill in all required fields to continue.',
              )}
            </Text>
          )}
          <Box display="flex" justifyContent="flex-end" gap={3}>
            <Button
              type="button"
              variant="outline"
              colorPalette="neutral"
              onClick={onCancel}
              disabled={isSubmitting}
              px={5}
            >
              {tr('users.form.button.cancel', 'Cancel')}
            </Button>
            {!isEdit && onSubmitAndCreateAnother && (
              <Button
                type="button"
                variant="outline"
                colorPalette="brand"
                onClick={handleCreateAnother}
                loading={isSubmitting}
                disabled={!canSubmit}
                px={5}
              >
                {tr(
                  'users.form.button.saveAndCreateAnother',
                  'Save & Create Another',
                )}
              </Button>
            )}
            <Button
              type="submit"
              colorPalette="brand"
              loading={isSubmitting}
              disabled={!canSubmit}
              px={5}
            >
              {isEdit
                ? tr('users.form.button.saveChanges', 'Save Changes')
                : tr('users.form.button.createUser', 'Create User')}
            </Button>
          </Box>
        </Box>
      </Card.Root>
    </form>
  )
}
